/** Configuration pour affichage du méga-menu */
const Config = {
  menuMaxWidth: 1440,
  sideImageWidth: 290,
  gridItemMinWidth: 240,
  subcategoryAmount: [0, 12, 8, 5],
};

/**
 * Approximation de l'espace systématiquement perdu via les différentes marges, ce qui
 * inclut les marge du site, du menu, les différents gap des grilles. Cette valeur pourrait
 * être calculé mais nécessiterait de sortir l'ensemble des marges du menu dans des variables.
 */
const LostSpace = {
  excludingAside: 160,
  includingAside: 160 + 24 + Config.sideImageWidth,
};

/**
 * Les breakpoints sont listés en dur, au lieu d'être calculé automatiquement, afin de ne pas
 * complexifier inutilement le code. Ils sont utilisés pour définir les différentes résolutions
 * pour lequelles un changement d'affichage du menu peut survenir
 */
const Breakpoints = [
  1240, // Résolution inférieur au breakpoint d'affichage du menu
  1434, // Image (290) + 4 Colonnes (4x240) + Marges diverses (184)
];

/**
 * Depuis une liste de valeurs représentant des breakpoints, retourne une liste
 * d'intervalles de résolution. Chaque intervalle est défini par son minimum
 * et son maximum, la dernière valeur n'ayant pas de maximum.
 */
export const getScreenRanges = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted.map((min, idx, arr) => {
    if (idx + 1 >= arr.length) return { min, max: null };
    return { min, max: arr[idx + 1] };
  });
};

/**
 * Retourne les dimensions d'une grille de largeur `freeSpace` dans laquelle on
 * souhaite afficher `itemCount` éléments de largeur `itemWidth`. Calcule tout
 * d'abord le nombre maximum de colonnes permettant de tout afficher, puis le
 * nombre minimum de lignes pour tout afficher. A partir du nombre de lignes,
 * le nombre de colonnes est alors ajuster pour minimiser l'espace vide.
 */
export const getGridSize = (args: { itemCount: number; itemWidth: number; freeSpace: number }) => {
  const maxCols = Math.floor(args.freeSpace / args.itemWidth);
  const rows = Math.ceil(args.itemCount / maxCols);
  const cols = Math.ceil(args.itemCount / rows);
  return { rows, cols };
};

/**
 * Pour un nombre de catégories donné, cette fonction retourne la configuration
 * d'affichage pour l'ensemble des breakpoints. Le retour contient le nombre de
 * colonnes et de lignes de la grille, ainsi que le nombre de sous-catégories à
 * afficher pour chacun des catégories. Retourne également la media-query associée.
 */
export const getLayout = (args: { count: number; hasImage: boolean }) => {
  const layouts = getScreenRanges(Breakpoints).map(range => {
    // Taille de la grille sans image d'accroche
    const gridSizeExcludingAside = getGridSize({
      itemCount: args.count,
      itemWidth: Config.gridItemMinWidth,
      freeSpace: range.min - LostSpace.excludingAside,
    });

    // Taille de la grille avec image d'accroche
    const gridSizeIncludingAside = getGridSize({
      itemCount: args.count,
      itemWidth: Config.gridItemMinWidth,
      freeSpace: range.min - LostSpace.includingAside,
    });

    // Accroche masquée si pas assez de place, ou si image manquante
    const showAside = args.hasImage && gridSizeIncludingAside.rows === 1;
    const aside = { show: showAside, width: Config.sideImageWidth };

    // Utilisation de la grille adaptée en fonction de la présence de l'accroche
    const { cols, rows } = aside.show ? gridSizeIncludingAside : gridSizeExcludingAside;
    const subs = Config.subcategoryAmount[rows] ?? Config.subcategoryAmount.at(-1);
    const grid = { cols, rows, subs };

    // Config pour la résolution en cours
    return { range, aside, grid };
  });

  // Largeur maximale du menu et retour de la config
  return { layouts, maxWidth: Config.menuMaxWidth };
};
